import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  LinearProgress,
  Grid
} from '@mui/material'
import {
  Add,
  Edit,
  Delete,
  Visibility,
  Refresh
} from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import api from '../../../services/api'

const DeficiencyMaster = () => {
  const { enqueueSnackbar } = useSnackbar()

  const [loading, setLoading] = useState(false)
  const [deficiencies, setDeficiencies] = useState([])
  const [totalRows, setTotalRows] = useState(0)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [openDialog, setOpenDialog] = useState(false)
  const [editingDeficiency, setEditingDeficiency] = useState(null)
  const [departments, setDepartments] = useState([])
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    category: 'IMPORTANT',
    department: '',
    subDepartment: '',
    documentType: 'ADMISSION_NOTE',
    resolutionTime: 24,
    escalationLevel: 1,
    responsibleRole: [],
    nabhReference: '',
    isCritical: false,
    autoAssignTo: '',
    isActive: true
  })

  useEffect(() => {
    loadDeficiencies()
    loadDepartments()
  }, [page, rowsPerPage])

  const loadDepartments = async () => {
    try {
      const response = await api.get('/department/type', {
        params: { type: "CLINICAL" }
      });
      const departments = response.data.data?.departments || response.data.data || []
      setDepartments(Array.isArray(departments) ? departments : [])
    } catch (error) {
      console.error('Failed to load departments:', error)
    }
  }

  const loadDeficiencies = async () => {
    setLoading(true)
    try {
      const response = await api.get('/mrd/deficiency/master', {
        params: {
          page: page + 1, // Backend expects 1-based page numbers
          limit: rowsPerPage,
          isActive: true
        }
      })

      if (response.data.success) {
        const data = response.data.data
        const deficiencies = data?.docs || data || []
        setDeficiencies(Array.isArray(deficiencies) ? deficiencies : [])

        // Set total count for pagination
        if (data?.totalDocs !== undefined) {
          setTotalRows(data.totalDocs)
        } else if (data?.total !== undefined) {
          setTotalRows(data.total)
        } else {
          setTotalRows(deficiencies.length)
        }


      } else {
        throw new Error('Failed to fetch deficiencies')
      }
    } catch (error) {
      console.error('Load deficiencies error:', error)
      enqueueSnackbar('Failed to load deficiencies', { variant: 'error' })
      setDeficiencies([])
      setTotalRows(0)
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    try {
      if (editingDeficiency) {
        // Update existing deficiency
        await api.put(`/mrd/deficiency/master/${editingDeficiency._id}`, formData)
        enqueueSnackbar('Deficiency updated successfully', { variant: 'success' })
      } else {
        // Create new deficiency
        await api.post('/mrd/deficiency/master', formData)
        enqueueSnackbar('Deficiency created successfully', { variant: 'success' })
      }

      setOpenDialog(false)
      setEditingDeficiency(null)
      setFormData({
        code: '',
        name: '',
        description: '',
        category: 'IMPORTANT',
        department: '',
        subDepartment: '',
        documentType: 'ADMISSION_NOTE',
        resolutionTime: 24,
        escalationLevel: 1,
        responsibleRole: [],
        nabhReference: '',
        isCritical: false,
        autoAssignTo: '',
        isActive: true
      })
      setPage(0) // Reset to first page
      loadDeficiencies()
    } catch (error) {
      console.error('Save deficiency error:', error)
      enqueueSnackbar('Failed to save deficiency', { variant: 'error' })
    }
  }

  const handleEdit = (deficiency) => {
    setEditingDeficiency(deficiency)
    setFormData({
      code: deficiency.code || '',
      name: deficiency.name || '',
      description: deficiency.description || '',
      category: deficiency.category || 'IMPORTANT',
      department: deficiency.department?._id || deficiency.department || '',
      subDepartment: deficiency.subDepartment || '',
      documentType: deficiency.documentType || 'ADMISSION_NOTE',
      resolutionTime: deficiency.resolutionTime || 24,
      escalationLevel: deficiency.escalationLevel || 1,
      responsibleRole: deficiency.responsibleRole || [],
      nabhReference: deficiency.nabhReference || '',
      isCritical: deficiency.isCritical || false,
      autoAssignTo: deficiency.autoAssignTo?._id || deficiency.autoAssignTo || '',
      isActive: deficiency.isActive !== undefined ? deficiency.isActive : true
    })
    setOpenDialog(true)
  }

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this deficiency?')) {
      try {
        await api.delete(`/mrd/deficiency/master/${id}`)
        enqueueSnackbar('Deficiency deleted successfully', { variant: 'success' })
        setPage(0) // Reset to first page
        loadDeficiencies()
      } catch (error) {
        console.error('Delete deficiency error:', error)
        enqueueSnackbar('Failed to delete deficiency', { variant: 'error' })
      }
    }
  }



  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" fontWeight={600}>
          Deficiency Master
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadDeficiencies}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setOpenDialog(true)}
          >
            Add Deficiency
          </Button>
        </Box>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Deficiencies Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Document Type</TableCell>
              <TableCell>Department</TableCell>
              <TableCell>Resolution Time</TableCell>
              <TableCell>Critical</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {deficiencies.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Alert severity="info">
                    No deficiencies found
                  </Alert>
                </TableCell>
              </TableRow>
            ) : (
              deficiencies.map((deficiency) => (
                <TableRow key={deficiency._id} hover>
                  <TableCell>
                    <Chip
                      label={deficiency.code}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>

                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {deficiency.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {deficiency.description}
                      </Typography>
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Chip
                      label={deficiency.category}
                      size="small"
                      color={deficiency.category === 'MANDATORY' ? 'error' :
                        deficiency.category === 'IMPORTANT' ? 'warning' : 'default'}
                    />
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2">
                      {deficiency.documentType?.replace(/_/g, ' ') || '-'}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2">
                      {deficiency.department?.department || '-'}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2">
                      {deficiency.resolutionTime}h
                    </Typography>
                  </TableCell>

                  <TableCell>
                    {deficiency.isCritical ? (
                      <Chip label="Critical" size="small" color="error" />
                    ) : (
                      <Chip label="Normal" size="small" color="default" />
                    )}
                  </TableCell>

                  <TableCell>
                    <Chip
                      label={deficiency.isActive ? 'Active' : 'Inactive'}
                      size="small"
                      color={deficiency.isActive ? 'success' : 'default'}
                    />
                  </TableCell>

                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(deficiency)}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(deficiency._id)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={totalRows}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10))
            setPage(0)
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingDeficiency ? 'Edit Deficiency' : 'Add New Deficiency'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Code"
                  value={formData.code}
                  onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value }))}
                  required
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={formData.category}
                    label="Category"
                    onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                  >
                    <MenuItem value="MANDATORY">Mandatory</MenuItem>
                    <MenuItem value="IMPORTANT">Important</MenuItem>
                    <MenuItem value="OPTIONAL">Optional</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Document Type</InputLabel>
                  <Select
                    value={formData.documentType}
                    label="Document Type"
                    onChange={(e) => setFormData(prev => ({ ...prev, documentType: e.target.value }))}
                  >
                    <MenuItem value="ADMISSION_NOTE">Admission Note</MenuItem>
                    <MenuItem value="DISCHARGE_SUMMARY">Discharge Summary</MenuItem>
                    <MenuItem value="CONSENT_FORM">Consent Form</MenuItem>
                    <MenuItem value="LAB_REPORT">Lab Report</MenuItem>
                    <MenuItem value="RADIOLOGY_REPORT">Radiology Report</MenuItem>
                    <MenuItem value="PRESCRIPTION">Prescription</MenuItem>
                    <MenuItem value="PROGRESS_NOTE">Progress Note</MenuItem>
                    <MenuItem value="OPERATIVE_NOTE">Operative Note</MenuItem>
                    <MenuItem value="OTHER">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Department</InputLabel>
                  <Select
                    value={formData.department}
                    label="Department"
                    onChange={(e) => setFormData(prev => ({ ...prev, department: e.target.value }))}
                  >
                    {departments.map((dept) => (
                      <MenuItem key={dept._id} value={dept._id}>
                        {dept.department}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Sub Department"
                  value={formData.subDepartment}
                  onChange={(e) => setFormData(prev => ({ ...prev, subDepartment: e.target.value }))}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Resolution Time (hours)"
                  value={formData.resolutionTime}
                  onChange={(e) => setFormData(prev => ({ ...prev, resolutionTime: parseInt(e.target.value) || 0 }))}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Escalation Level"
                  value={formData.escalationLevel}
                  onChange={(e) => setFormData(prev => ({ ...prev, escalationLevel: parseInt(e.target.value) || 1 }))}
                  inputProps={{ min: 1, max: 3 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Responsible Roles</InputLabel>
                  <Select
                    multiple
                    value={formData.responsibleRole}
                    label="Responsible Roles"
                    onChange={(e) => setFormData(prev => ({ ...prev, responsibleRole: e.target.value }))}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    <MenuItem value="DOCTOR">Doctor</MenuItem>
                    <MenuItem value="NURSE">Nurse</MenuItem>
                    <MenuItem value="LAB_TECH">Lab Technician</MenuItem>
                    <MenuItem value="PHARMACIST">Pharmacist</MenuItem>
                    <MenuItem value="RADIOLOGIST">Radiologist</MenuItem>
                    <MenuItem value="ANESTHETIST">Anesthetist</MenuItem>
                    <MenuItem value="MRD_STAFF">MRD Staff</MenuItem>
                    <MenuItem value="ADMIN">Admin</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Auto Assign To (Role ID)"
                  value={formData.autoAssignTo}
                  onChange={(e) => setFormData(prev => ({ ...prev, autoAssignTo: e.target.value }))}
                  placeholder="Role ID for auto assignment"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="NABH Reference"
                  value={formData.nabhReference}
                  onChange={(e) => setFormData(prev => ({ ...prev, nabhReference: e.target.value }))}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Critical</InputLabel>
                  <Select
                    value={formData.isCritical}
                    label="Critical"
                    onChange={(e) => setFormData(prev => ({ ...prev, isCritical: e.target.value }))}
                  >
                    <MenuItem value={false}>No</MenuItem>
                    <MenuItem value={true}>Yes</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.isActive}
                    label="Status"
                    onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.value }))}
                  >
                    <MenuItem value={true}>Active</MenuItem>
                    <MenuItem value={false}>Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default DeficiencyMaster